import {
  IAdminUser,
  IAdminUserInvitation
} from '@dadohr/shared/domainTypes/adminUser';
import { ICurrentUser } from '@dadohr/shared/domainTypes/currentUser';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import { ExperienceTemplateStatus } from '@dadohr/shared/domainTypes/experience';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import {
  USER_ROLE,
  UserLevelScope
} from '@dadohr/shared/domainTypes/permissions';
import { CF } from '@dadohr/shared/versions';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { keyBy, sortBy } from 'lodash';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { getAppUrl } from '../../../../admin/services/routes';
import {
  AdditionActionsMenuOption,
  AdditionalActionsMenu
} from '../../../../components/AdditionalActionsMenu';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { ColoredChip } from '../../../../components/ColoredChip';
import { DialogActionsWithSlots } from '../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../components/Form/FormRow';
import {
  ItemSorter,
  ItemSorters,
  ROW_HEIGHTS,
  RowsRenderer
} from '../../../../components/GroupableList';
import { Loader } from '../../../../components/Loader';
import { NoAccess } from '../../../../components/NoAccess';
import { RadioPanel } from '../../../../components/RadioPanel';
import { IColumn } from '../../../../components/Table/VirtualizedSortable';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useErrorLogger } from '../../../../hooks/useErrorLogger';
import { useRouter } from '../../../../hooks/useRouter';
import { useSnackbar } from '../../../../hooks/useSnackbar';
import { useSort } from '../../../../hooks/useSort';
import { UnsafeTFunction, useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import { useRoutes } from '../../../../routes';
import {
  useAdminUserInvitationsInOrg,
  useAdminUsersInOrg
} from '../../../../services/adminUsers';
import { useCurrentUser } from '../../../../services/currentUser';
import { useExperienceTemplatesByOrgId } from '../../../../services/experiences/template';
import {
  combineLoadingValues,
  useMappedLoadingValue
} from '../../../../services/firebase';
import { callFirebaseFunction } from '../../../../services/firebaseFunctions';
import { LightMdToJsx } from '../../../../services/formatter';
import { isFreeTierOrg } from '../../../../services/organizationTier';
import { hasPermissionToAccessPage } from '../../../../services/permissions';
import { toFullName } from '../../../../services/user';
import { useOrgSideNav } from '../sideNav';

const SHOW_DADO_EMPLOYEES = false;

type UserArgs = {
  email: string;
  firstName: string;
  lastName: string;
};

const inviteUser = async (
  inviterId: string,
  orgId: string,
  userArgs: UserArgs,
  role: USER_ROLE,
  additionalExperienceTemplateScopes: {
    [templateId: string]: ('view' | 'edit' | 'launch')[];
  }
) => {
  await callFirebaseFunction(CF.adminUser.createInvitation, {
    orgId,
    user: {
      firstName: userArgs.firstName.trim(),
      preferredName: userArgs.firstName.trim(),
      middleName: '',
      lastName: userArgs.lastName.trim(),
      email: userArgs.email.trim()
    },
    role,
    scopes: [],
    additionalExperienceTemplateScopes,
    invitedBy: inviterId,
    triggerMail: true
  });
};

const editUser = async (
  orgId: string,
  user: {
    id: string;
    firstName: string;
    lastName: string;
  },
  role: USER_ROLE,
  scopes: UserLevelScope[],
  additionalExperienceTemplateScopes: {
    [templateId: string]: ('view' | 'edit' | 'launch')[];
  }
) => {
  await callFirebaseFunction('adminUser-editAdminUser', {
    orgId,
    user: {
      id: user.id,
      firstName: user.firstName,
      preferredName: user.firstName,
      middleName: '',
      lastName: user.lastName
    },
    role: role,
    scopes: scopes,
    additionalExperienceTemplateScopes
  });
};

type AdminUserFormState = {
  user: {
    email: string;
    firstName: string;
    lastName: string;
  };
  role: USER_ROLE;
  scopes: UserLevelScope[];
  additionalExperienceTemplateScopes: {
    [templateId: string]: ('view' | 'edit' | 'launch')[];
  };
};

const INITIAL_ADMIN_USER_FORM_STATE = (
  role: USER_ROLE
): AdminUserFormState => ({
  user: {
    email: '',
    firstName: '',
    lastName: ''
  },
  role,
  scopes: [],
  additionalExperienceTemplateScopes: {}
});

const AdminUserForm = ({
  value,
  onChange,
  orgId,
  allowEmailChanges = false,
  restrictRoleAndScopeChanges
}: {
  value: AdminUserFormState;
  onChange: (nextValue: AdminUserFormState) => void;
  orgId: string;
  allowEmailChanges?: boolean;
  restrictRoleAndScopeChanges: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const [allTpls, loading] = useExperienceTemplatesByOrgId(orgId);
  const tpls = useMemo(
    () =>
      allTpls?.filter(
        (template) =>
          template.data.status !== ExperienceTemplateStatus.ARCHIVED
      ),
    [allTpls]
  );

  return (
    <>
      <FormRow>
        <TextField
          label="Email"
          value={value.user.email}
          disabled={!allowEmailChanges}
          onChange={(ev) =>
            onChange({
              ...value,
              user: { ...value.user, email: ev.target.value }
            })
          }
          fullWidth
          variant="outlined"
        />
      </FormRow>
      <FormRow>
        <TextField
          label="First Name"
          value={value.user.firstName}
          onChange={(ev) =>
            onChange({
              ...value,
              user: { ...value.user, firstName: ev.target.value }
            })
          }
          fullWidth
          variant="outlined"
        />
        <TextField
          label="Last Name"
          value={value.user.lastName}
          onChange={(ev) =>
            onChange({
              ...value,
              user: { ...value.user, lastName: ev.target.value }
            })
          }
          fullWidth
          variant="outlined"
        />
      </FormRow>
      {!restrictRoleAndScopeChanges && (
        <RadioPanel
          value={value.role}
          orientation="vertical"
          onChange={(role) => {
            onChange({ ...value, role });
          }}
          options={[
            {
              value: USER_ROLE.ACCOUNT_ADMIN,
              label: t('Account Administrator'),
              description: t(
                'Can view and manage all experiences, import users, and invite admins.'
              )
            },
            {
              value: USER_ROLE.EXPERIENCE_MANAGER,
              label: t('Experience Manager'),
              description: t('Can view and edit selected experiences.'),
              body: value.role === USER_ROLE.EXPERIENCE_MANAGER && (
                <>
                  {loading && <Loader height={10} />}
                  {tpls &&
                    (tpls.length === 0 ? (
                      <Typography align="center">
                        {' '}
                        {t(
                          "There aren't any experiences for your organization"
                        )}
                      </Typography>
                    ) : (
                      <FlexContainer
                        fullWidth={true}
                        spacing={0}
                        direction="column"
                        style={{
                          height: '150px',
                          ...(tpls.length > 3 && { overflowY: 'scroll' })
                        }}
                      >
                        {sortBy(
                          tpls,
                          (template) =>
                            template.data.adminTitle || template.data.title
                        ).map((template) => {
                          return (
                            <>
                              <ExperienceManagerExperienceList
                                key={template.id}
                                title={
                                  template.data.adminTitle ||
                                  template.data.title
                                }
                                value={
                                  value.additionalExperienceTemplateScopes[
                                    template.id
                                  ] || []
                                }
                                onChange={(nextValue) => {
                                  onChange({
                                    ...value,
                                    additionalExperienceTemplateScopes: {
                                      ...value.additionalExperienceTemplateScopes,
                                      [template.id]: nextValue
                                    }
                                  });
                                }}
                              />
                              <Divider light style={{ width: '100%' }} />
                            </>
                          );
                        })}
                      </FlexContainer>
                    ))}
                </>
              )
            }
          ]}
        />
      )}
    </>
  );
};

const EditAdminUserDialogInner = ({
  adminUser,
  org,
  onClose
}: {
  adminUser: Doc<IAdminUser>;
  org: IOrganization;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const [state, setState] = useState<AdminUserFormState>({
    user: {
      email: adminUser.data.email,
      firstName: adminUser.data.firstName,
      lastName: adminUser.data.lastName
    },
    role:
      org.permissions[adminUser.id]?.v2.role || USER_ROLE.EXPERIENCE_MANAGER,

    scopes: org.permissions[adminUser.id]?.v2.scopes || [],
    additionalExperienceTemplateScopes:
      org.permissions[adminUser.id]?.v2.additionalExpTplScopes || {}
  });
  const { enqueueSnackbarSuccess } = useSnackbar();

  useEffect(() => {
    setState({
      user: {
        email: adminUser.data.email,
        firstName: adminUser.data.firstName,
        lastName: adminUser.data.lastName
      },
      role:
        org.permissions[adminUser.id]?.v2.role || USER_ROLE.EXPERIENCE_MANAGER,
      scopes: org.permissions[adminUser.id]?.v2.scopes || [],
      additionalExperienceTemplateScopes:
        org.permissions[adminUser.id]?.v2.additionalExpTplScopes || {}
    });
  }, [adminUser, org]);
  return (
    <>
      <DialogTitleWithTypography>{t('Edit Admin')}</DialogTitleWithTypography>
      <DialogContent style={{ maxHeight: '75vh', overflow: 'hidden' }}>
        <AdminUserForm
          value={state}
          onChange={setState}
          orgId={org.id}
          restrictRoleAndScopeChanges={isFreeTierOrg(org)}
        />
      </DialogContent>
      <DialogActionsWithSlots
        right={
          <>
            <Button color="primary" onClick={onClose}>
              Cancel
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              disabled={
                !state.user.email ||
                !state.user.firstName ||
                !state.user.lastName
              }
              onClick={async () => {
                return editUser(
                  org.id,
                  {
                    id: adminUser.id,
                    ...state.user
                  },
                  state.role,
                  state.role === USER_ROLE.EXPERIENCE_MANAGER
                    ? []
                    : state.scopes,
                  state.additionalExperienceTemplateScopes
                ).then(() => {
                  enqueueSnackbarSuccess('User updated!');
                  onClose();
                });
              }}
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </>
  );
};

export const InviteAdminUserDialog = ({
  open,
  onClose,
  currentUser,
  currentAdmins
}: {
  open: boolean;
  onClose: () => void;
  currentUser: ICurrentUser;
  currentAdmins: Doc<IAdminUser>[];
}) => {
  const [state, setState] = useState<AdminUserFormState>(
    INITIAL_ADMIN_USER_FORM_STATE(
      isFreeTierOrg(currentUser.org)
        ? USER_ROLE.FREE_TIER_ACCOUNT_ADMIN
        : USER_ROLE.ACCOUNT_ADMIN
    )
  );
  const { enqueueSnackbarSuccess, enqueueSnackbarError } = useSnackbar();
  const { t } = useUnsafeTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{ style: { maxWidth: 850 } }}
    >
      <DialogTitleWithTypography>
        {t('Invite New Admin')}
      </DialogTitleWithTypography>
      <DialogContent style={{ maxHeight: '75vh', overflow: 'hidden' }}>
        <AdminUserForm
          value={state}
          onChange={setState}
          orgId={currentUser.org.id}
          restrictRoleAndScopeChanges={isFreeTierOrg(currentUser.org)}
          allowEmailChanges
        />
      </DialogContent>
      <DialogActionsWithSlots
        right={
          <>
            <Button color="primary" onClick={onClose}>
              Cancel
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              disabled={
                !state.user.email ||
                !state.user.firstName ||
                !state.user.lastName ||
                !!currentAdmins.find((a) => a.data.email === state.user.email)
              }
              onClick={async () => {
                console.log('STATE', state);
                return inviteUser(
                  currentUser.id,
                  currentUser.org.id,
                  state.user,
                  state.role,
                  state.additionalExperienceTemplateScopes
                )
                  .then(() => {
                    enqueueSnackbarSuccess(t('Invitation created!'));
                    onClose();
                    setState(
                      INITIAL_ADMIN_USER_FORM_STATE(
                        isFreeTierOrg(currentUser.org)
                          ? USER_ROLE.FREE_TIER_ACCOUNT_ADMIN
                          : USER_ROLE.ACCOUNT_ADMIN
                      )
                    );
                  })
                  .catch((err) => {
                    console.log('ERR', err);
                    if (err?.message === 'INVALID_EMAIL') {
                      enqueueSnackbarError(
                        t(
                          'Invalid email. Please check your inputs and try again.'
                        )
                      );
                    } else {
                      enqueueSnackbarError(
                        t(
                          'Something went wrong. Please check your inputs and try again.'
                        )
                      );
                    }
                  });
              }}
              pending="Inviting..."
            >
              {t('Send invitation email')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

type AdminUserOrInvitation =
  | {
      type: 'ADMIN_USER';
      d: Doc<IAdminUser>;
    }
  | {
      type: 'INVITATION';
      d: Doc<IAdminUserInvitation>;
    };

type ColumnName = 'name' | 'email' | 'status' | 'role' | 'actions';
type D = AdminUserOrInvitation;
type O = {
  org: IOrganization;
  currentUser: ICurrentUser;
  t: UnsafeTFunction;
};
type Column = IColumn<D, ColumnName, O>;

const Actions = ({ d, o }: { d: D; o: O }) => {
  const { enqueueSnackbarSuccess } = useSnackbar();
  const { ROUTES } = useRoutes();
  const { t } = useUnsafeTranslation();
  const options: AdditionActionsMenuOption[] = useMemo(() => {
    if (d.type === 'INVITATION') {
      return [
        {
          key: 'copyLink',
          label: t('Copy Invitation Link'),
          onClick: () => {
            const url = `${getAppUrl()}${ROUTES.acceptInvitation.url(d.d.id)}`;
            navigator.clipboard.writeText(url);
            enqueueSnackbarSuccess(
              t('Invitation Link copied to clipboard!') + ` - ${url}`
            );
          }
        },
        {
          key: 'revokeInvitation',
          label: t('Revoke Invitation'),
          onClick: () =>
            callFirebaseFunction(CF.adminUser.revokeInvitation, {
              invitationId: d.d.id
            }).then(() => {
              enqueueSnackbarSuccess(t('Invitation revoked!'));
            })
        }
      ];
    }
    return [
      {
        key: 'edit',
        label: t('Edit...'),
        dialog: ({ onClose }) => {
          return (
            <EditAdminUserDialogInner
              adminUser={d.d}
              org={o.org}
              onClose={onClose}
            />
          );
        }
      },
      {
        key: 'removeFromOrg',
        label: t('Remove from Organization'),
        dialog: ({ onClose }) => {
          return (
            <>
              <DialogTitle>{t('Remove from Organization')}?</DialogTitle>
              <DialogContent>
                <LightMdToJsx>
                  {t(
                    'Please confirm to remove **{{ name }}** from **{{ org }}**.',
                    {
                      name: `${toFullName(d.d.data)} (${d.d.data.email})`,
                      org: o.org.name
                    }
                  )}
                </LightMdToJsx>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose}>{t('Cancel')}</Button>
                <ButtonWithPromise
                  variant="contained"
                  color="secondary"
                  onClick={() =>
                    callFirebaseFunction(CF.adminUser.removeFromOrg, {
                      orgId: o.org.id,
                      adminId: d.d.id
                    }).then(() => {
                      enqueueSnackbarSuccess(
                        t('{{ name }} is no longer an admin for {{ org }}.', {
                          name: toFullName(d.d.data),
                          org: o.org.name
                        })
                      );
                      onClose();
                    })
                  }
                  pending={`${t('Removing')}...`}
                >
                  {t('Remove')}
                </ButtonWithPromise>
              </DialogActions>
            </>
          );
        }
      }
    ];
  }, [d, o.org, ROUTES.acceptInvitation, enqueueSnackbarSuccess, t]);
  return <AdditionalActionsMenu options={options} />;
};

const COLUMNS: Column[] = [
  {
    key: 'name',
    head: () => 'Name',
    cell: (d, o) => {
      const name = toFullName(d.d.data);
      return d.d.data.email === o.currentUser.email
        ? `${name} (${o.t('you')})`
        : name;
    },
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'email',
    head: () => 'Email',
    cell: (d) => d.d.data.email,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'status',
    head: () => 'Status',
    cell: (d, o) => {
      if (d.type === 'ADMIN_USER') {
        return (
          <ColoredChip size="small" mode="light" color="green">
            {o.t('Active')}
          </ColoredChip>
        );
      }
      if (d.type === 'INVITATION') {
        return (
          <ColoredChip size="small" mode="light" color="blue">
            {o.t('Invited')}
          </ColoredChip>
        );
      }
      return null;
    },
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 150,
    flexGrow: 1
  },
  {
    key: 'role',
    head: () => 'Role',
    cell: (d, o) => {
      const role =
        (d.type === 'ADMIN_USER'
          ? o.org.permissions[d.d.id]?.v2.role
          : d.type === 'INVITATION'
          ? d.d.data.role
          : null) || USER_ROLE.EXPERIENCE_MANAGER;
      const additionalExpTplScopes = Object.values(
        d.type === 'INVITATION'
          ? d.d.data.additionalExperienceTemplateScopes || {}
          : d.type === 'ADMIN_USER'
          ? o.org.permissions[d.d.id]?.v2.additionalExpTplScopes || {}
          : {}
      ).filter((x) => x.length).length;
      const roleAsString =
        role === USER_ROLE.ACCOUNT_ADMIN ||
        role === USER_ROLE.FREE_TIER_ACCOUNT_ADMIN
          ? o.t('Account Administrator')
          : role === USER_ROLE.EXPERIENCE_MANAGER
          ? o.t('Experience Manager')
          : '';
      if (roleAsString) {
        return (
          <FlexContainer>
            <div>{roleAsString}</div>
            {role === USER_ROLE.EXPERIENCE_MANAGER && (
              <ColoredChip mode="light" color="gray" size="small">
                {additionalExpTplScopes}
              </ColoredChip>
            )}
          </FlexContainer>
        );
      }
      return null;
    },
    align: 'left',
    sortable: false,
    width: 150,
    flexGrow: 1
  },
  {
    key: 'actions',
    head: () => '',
    alternateHead: () => '',
    cell: (d, o) => <Actions d={d} o={o} />,
    sortable: false,
    align: 'right',
    width: 50,
    flexGrow: 0,
    showOverflowOnHover: false
  }
];

const SORTERS: ItemSorters<D, O> = {
  email: {
    key: 'email',
    items: { sort: (d) => d.d.data.email, dir: 'asc' }
  },
  name: {
    key: 'name',
    items: { sort: (d) => toFullName(d.d.data), dir: 'asc' }
  },
  status: {
    key: 'status',
    items: { sort: (d) => ['ACTIVE', 'INVITATION'].indexOf(d.type), dir: 'asc' }
  }
};

const DEFAULT_SORTER = SORTERS.email;

const AdminUserList = ({ org }: { org: IOrganization }) => {
  const { t } = useUnsafeTranslation();
  const currentUser = useCurrentUser();
  const [ds, loading, error] = useMappedLoadingValue(
    combineLoadingValues(
      useAdminUsersInOrg(org.id),
      useAdminUserInvitationsInOrg(org.id)
    ),
    ([as, is]): AdminUserOrInvitation[] => {
      const adminUsersByEmail = keyBy(as, (a) => a.data.email);
      const res: AdminUserOrInvitation[] = [];
      as.forEach((a) => {
        res.push({ type: 'ADMIN_USER', d: a });
      });
      is.forEach((i) => {
        if (!adminUsersByEmail[i.data.email] && i.data.status === 'PENDING') {
          res.push({ type: 'INVITATION', d: i });
        }
      });
      return res.filter((d) => {
        const { email } = d.d.data;
        return (
          SHOW_DADO_EMPLOYEES ||
          (!email.endsWith('dadohr.com') &&
            !email.match(/(gernot|arielle|gabriel).emplexp@gmail.com/))
        );
      });
    }
  );
  console.log(ds);
  useErrorLogger('admin users & invitations', error);
  const otherProps: O = useMemo(
    () => ({
      org,
      currentUser,
      t
    }),
    [org, currentUser, t]
  );

  const { sortBy, direction, setSort } = useSort<ItemSorter<D, O>>(
    DEFAULT_SORTER,
    DEFAULT_SORTER.items.dir
  );
  return (
    <>
      {(loading || !ds) && <Loader height={400} />}
      {!!ds && (
        <RowsRenderer
          variant="contained"
          columns={COLUMNS}
          rows={ds}
          rowToKey={(d) => d.d.id}
          sorter={sortBy}
          sortDirection={direction}
          onHeadClick={(c, dir) => {
            const sorter = SORTERS[c.key];
            if (!sorter) {
              return;
            }
            setSort(sorter, dir || sorter.items.dir);
          }}
          renderHead={true}
          headProps={{ sticky: true }}
          chunkSize={30}
          rootMargin="300px"
          rowHeight={ROW_HEIGHTS.dense}
          otherProps={otherProps}
        />
      )}
    </>
  );
};

const ExperienceManagerExperienceList = ({
  title,
  value,
  onChange
}: {
  title: string;
  value: ('view' | 'edit' | 'launch')[];
  onChange: (nextValue: ('view' | 'edit' | 'launch')[]) => void;
}) => {
  return (
    <FormControl component="fieldset" variant="standard" fullWidth>
      <FormGroup
        row
        style={{ justifyContent: 'space-between', alignItems: 'center' }}
      >
        <Typography
          variant="body1"
          noWrap
          style={{ flex: 1, minWidth: 0, maxWidth: '500px', marginRight: 8 }}
        >
          {title}
        </Typography>
        <FormGroup row style={{ flexShrink: 0, flexWrap: 'nowrap' }}>
          <FormControlLabel
            control={
              <Switch
                name="view"
                onChange={() => {
                  if (value.includes('view')) {
                    onChange([]);
                  } else {
                    onChange(['view']);
                  }
                }}
                checked={value.includes('view')}
                disableRipple
              />
            }
            label="View"
          />
          <FormControlLabel
            control={
              <Switch
                name="edit"
                onChange={() => {
                  if (value.includes('edit')) {
                    onChange(value.filter((x) => x !== 'edit'));
                  } else {
                    onChange([...value, 'edit']);
                  }
                }}
                checked={value.includes('edit')}
                disabled={!value.includes('view')}
                disableRipple
              />
            }
            label="Edit"
          />
        </FormGroup>
      </FormGroup>
    </FormControl>
  );
};

const ContentOrgAdminUsers = () => {
  const { t } = useUnsafeTranslation();
  const sideNav = useOrgSideNav();
  const currentUser = useCurrentUser();
  const { org } = currentUser;
  const [adminUsers] = useAdminUsersInOrg(org.id);
  const currentAdmins = adminUsers ? adminUsers : EMPTY_ARR;
  const { location, history } = useRouter();

  const inviteDialog = useDialogState(false);

  // Auto-open invite dialog if ?invite=true is in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('invite') === 'true') {
      inviteDialog.openDialog();

      params.delete('invite');
      history.replace({ ...location, search: params.toString() });
    }
  }, [location, history, inviteDialog]);

  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <PageToolbar>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('pageTitle.orgAdminUsers')}
          </Typography>
        </PageToolbarTitle>
        <PageToolbarActions>
          <Button
            variant="contained"
            color="primary"
            onClick={inviteDialog.openDialog}
            startIcon={<Plus size={20} />}
          >
            {t('Invite new admin')}
          </Button>
        </PageToolbarActions>
      </PageToolbar>

      <Section>
        <AdminUserList org={org} />
      </Section>
      <InviteAdminUserDialog
        open={inviteDialog.dialogOpen}
        onClose={inviteDialog.closeDialog}
        currentUser={currentUser}
        currentAdmins={currentAdmins}
      />
    </PageBody>
  );
};

export const PageOrgAdminUsers = () => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.org.adminUsers)) {
    return <NoAccess />;
  }

  return <ContentOrgAdminUsers />;
};
